import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Stack,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import SummarizeIcon from "@mui/icons-material/Summarize";
import axios from "../../../config/axiosConfig";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import EquiposAsignadosOT from "./EquiposAsignadosOT";
import CentroEquipoDetalleDialog from "../../requerimiento/components/CentroEquipoDetalleDialog";

// Igual que cargaDetalle.php: una lista por tipo (Radio/Bateria) con checkbox por
// fila ("A" en el PHP viejo) que habilita los campos de esa fila. La selección se
// mantiene al cambiar de pestaña (Radio/Bateria) para poder armar la orden
// combinando ambos tipos antes de un solo "Adicionar". El panel lateral muestra
// los equipos YA asignados a la OT (Radios/Baterías asignados), no un preview de
// la selección pendiente. Paginado en backend + frontend para no traer todo el
// stock inactivo de golpe.
const PAGE_SIZE = 15;

const controlSx = {
  "& .MuiInputLabel-root": { fontSize: 11 },
  "& .MuiOutlinedInput-root": { height: 30, fontSize: 11.5 },
};

const headCellSx = {
  fontWeight: 800,
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};

// Input nativo (Box component="input") en vez de TextField para las celdas de la
// tabla paginada de equipos disponibles — mismo criterio que AsignarEquiposVariosOT.
const inputCompactoSx = {
  fontSize: 10.5,
  fontFamily: "inherit",
  padding: "4px 6px",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 0,
  outline: "none",
  bgcolor: "background.paper",
  color: "text.primary",
  "&:focus": { borderColor: "primary.main" },
  "&:disabled": { bgcolor: (t) => t.palette.action.disabledBackground, color: "text.disabled" },
};

const claveDe = (tipo, id) => `${tipo}-${id}`;

// Misma idea que SectionPaper en ReporteClienteResumenPage.jsx: ícono de color +
// borde superior de acento, para distinguir de un vistazo las 3 tarjetas.
const SectionCard = ({ title, icon, color = "primary", extra, children }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.25,
      borderRadius: 1,
      borderTop: "3px solid",
      borderTopColor: `${color}.main`,
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: "50%",
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
          bgcolor: (theme) => alpha(theme.palette[color].main, theme.palette.mode === "dark" ? 0.28 : 0.12),
          color: `${color}.main`,
        }}
      >
        {icon}
      </Box>
      <Typography variant="subtitle2" fontWeight={800}>{title}</Typography>
      {extra}
    </Stack>
    {children}
  </Paper>
);

const AsignarEquiposParOT = ({ cod_ot, cod_cliente, cod_servicio_cliente, onEquiposActualizados, onAsignadosCambiados }) => {
  const { user } = useAuth();
  const [tipo, setTipo] = useState("Radio"); // "Radio" | "Bateria"
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [buscar, setBuscar] = useState("");
  const [buscarAplicado, setBuscarAplicado] = useState("");
  const [loading, setLoading] = useState(false);
  // { [`${tipo}-${id}`]: { tipo, id, serie, grupo, placa, id_ref, observacion } }
  const [seleccion, setSeleccion] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [asignados, setAsignados] = useState({ radios: [], baterias: [] });
  const [equipoDialog, setEquipoDialog] = useState(null);

  useEffect(() => {
    onAsignadosCambiados?.(asignados.radios.length + asignados.baterias.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asignados]);
  const equiposAsignadosRef = useRef();

  const endpoint = tipo === "Radio" ? "/api/equipos-radio" : "/api/equipos-bateria";
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const cargar = (nextPage = page, buscarTexto = buscarAplicado) => {
    setLoading(true);
    axios
      .get(endpoint, {
        params: { estado: "Inactivo", ciudad: user.sucursal, buscar: buscarTexto, page: nextPage, pageSize: PAGE_SIZE },
      })
      .then((res) => {
        const data = res.data || {};
        setItems((data.items || []).map((e) => ({ ...e, id: e.ID })));
        setTotal(Number(data.total || 0));
        setPage(Number(data.page || nextPage));
      })
      .catch((err) => console.error("Error al cargar equipos", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    cargar(1, buscarAplicado);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, user.sucursal]);

  const seleccionEntries = Object.entries(seleccion);

  // La selección ya no se limpia al cambiar de pestaña: se puede armar la orden
  // combinando radios y baterías antes de "Adicionar".
  const toggle = (row) => {
    const key = claveDe(tipo, row.id);
    setSeleccion((prev) => {
      if (prev[key]) {
        const { [key]: _quitado, ...resto } = prev;
        return resto;
      }
      return {
        ...prev,
        [key]: { tipo, id: row.id, serie: row.serie, grupo: "", placa: "", id_ref: "", observacion: "" },
      };
    });
  };

  const setCampo = (key, campo, valor) => {
    setSeleccion((prev) => ({ ...prev, [key]: { ...prev[key], [campo]: valor } }));
  };

  const cambiarTipo = (nuevo) => {
    setTipo(nuevo);
    setBuscar("");
    setBuscarAplicado("");
  };

  const handleBuscar = () => {
    setBuscarAplicado(buscar.trim());
    cargar(1, buscar.trim());
  };

  const adicionar = async () => {
    if (!seleccionEntries.length) return;
    setGuardando(true);
    try {
      for (const [, datos] of seleccionEntries) {
        if (!datos?.serie) continue;

        if (datos.tipo === "Radio") {
          await axios.put(`/api/equipos-radio/${datos.id}`, {
            estado: "Asignado",
            cod_servicio: cod_servicio_cliente,
            asignado: cod_cliente,
            grupo: datos.grupo || "",
            placa: datos.placa || "",
            id_ref: datos.id_ref || "",
          });
          await axios.post(`/api/historial-radio/`, {
            serie: datos.serie,
            cod_ot,
            propiedad: "TRS",
            asignado: cod_cliente,
            ciudad: user.sucursal,
            estado: "Asignado",
            operacion: "Adicion",
            grupo: datos.grupo || "",
            placa: datos.placa || "",
            id_ref: datos.id_ref || "",
            usu: user.username,
          });
        } else {
          await axios.put(`/api/equipos-bateria/${datos.id}`, {
            estado: "Asignado",
            cod_servicio: cod_servicio_cliente,
            asignado: cod_cliente,
          });
          await axios.post(`/api/historial-bateria/`, {
            serie: datos.serie,
            cod_ot,
            propiedad: "TRS",
            asignado: cod_cliente,
            ciudad: user.sucursal,
            estado: "Asignado",
            operacion: "Adicion",
            observacion: datos.observacion || "",
            usu: user.username,
          });
        }
      }

      setSeleccion({});
      setOpenSnackbar(true);
      cargar(page, buscarAplicado);
      equiposAsignadosRef.current?.refetch();
      if (onEquiposActualizados) onEquiposActualizados();
    } catch (err) {
      console.error("Error al asignar equipos:", err);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={1.5}>
        {/* ── Columna izquierda: inventario disponible ── */}
        <Grid size={{ xs: 12, md: 8 }}>
          <SectionCard title="Inventario Disponible" icon={<Inventory2Icon fontSize="small" />} color="primary">
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                color="primary"
                value={tipo}
                onChange={(e, nuevo) => nuevo && cambiarTipo(nuevo)}
                sx={{ height: 30 }}
              >
                <ToggleButton value="Radio" sx={{ fontSize: 11.5, px: 1.5, py: 0.35, textTransform: "none" }}>
                  Radios
                </ToggleButton>
                <ToggleButton value="Bateria" sx={{ fontSize: 11.5, px: 1.5, py: 0.35, textTransform: "none" }}>
                  Baterías
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              alignItems="center"
              sx={{ mb: 1, rowGap: 1 }}
            >
              <TextField
                size="small"
                label="Buscar serie"
                value={buscar}
                onChange={(e) => setBuscar(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleBuscar();
                }}
                sx={{ minWidth: 160, ...controlSx }}
              />
              <IconButton size="small" onClick={handleBuscar} title="Buscar">
                <SearchIcon fontSize="small" />
              </IconButton>
              <Chip size="small" label={`Total: ${total}`} sx={{ height: 20, fontSize: 11 }} />
              <Chip size="small" color="warning" variant="outlined" label={`Seleccionados: ${seleccionEntries.length}`} sx={{ height: 20, fontSize: 11 }} />
              <Box sx={{ flexGrow: 1 }} />
              <Button
                size="small"
                variant="contained"
                color="success"
                disabled={!seleccionEntries.length || guardando}
                onClick={adicionar}
                sx={{ minHeight: 26, height: 26, fontSize: 11, whiteSpace: "nowrap", px: 1.5 }}
              >
                {guardando ? <CircularProgress size={16} color="inherit" /> : "Adicionar Seleccionados"}
              </Button>
            </Stack>

            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0.5 }}>
              <Table
                size="small"
                sx={{
                  tableLayout: "fixed",
                  "& .MuiTableCell-root": { py: 0.15, px: 0.6, fontSize: 10.5, lineHeight: 1.3 },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...headCellSx, width: 32 }}>Sel.</TableCell>
                    <TableCell sx={{ ...headCellSx, width: 130 }}>Serie</TableCell>
                    <TableCell sx={{ ...headCellSx, width: 130 }}>Modelo</TableCell>
                    {tipo === "Radio" ? (
                      <>
                        <TableCell sx={{ ...headCellSx, width: 80 }}>Grupo</TableCell>
                        <TableCell sx={{ ...headCellSx, width: 80 }}>ID Cliente</TableCell>
                        <TableCell sx={{ ...headCellSx, width: 80 }}>Placa</TableCell>
                      </>
                    ) : (
                      <TableCell sx={headCellSx}>Observación</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={tipo === "Radio" ? 6 : 4} align="center" sx={{ py: 2 }}>
                        <CircularProgress size={18} />
                      </TableCell>
                    </TableRow>
                  ) : !items.length ? (
                    <TableRow>
                      <TableCell colSpan={tipo === "Radio" ? 6 : 4}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                          No hay {tipo === "Radio" ? "radios" : "baterías"} inactivas en {user.sucursal || "tu sucursal"}.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((row) => {
                      const key = claveDe(tipo, row.id);
                      const checked = Boolean(seleccion[key]);
                      return (
                        <TableRow key={row.id} hover selected={checked}>
                          <TableCell>
                            <Checkbox size="small" checked={checked} onChange={() => toggle(row)} sx={{ p: 0 }} />
                          </TableCell>
                          <TableCell>
                            <Box
                              component="span"
                              onClick={() => setEquipoDialog({ tipo, identificador: row.serie })}
                              sx={{ cursor: "pointer", color: "primary.main", textDecoration: "underline" }}
                            >
                              {row.serie}
                            </Box>
                          </TableCell>
                          <TableCell>{row.modelo}</TableCell>
                          {tipo === "Radio" ? (
                            <>
                              <TableCell>
                                <Box
                                  component="input"
                                  disabled={!checked}
                                  value={seleccion[key]?.grupo || ""}
                                  onChange={(e) => setCampo(key, "grupo", e.target.value)}
                                  sx={{ ...inputCompactoSx, width: 60 }}
                                />
                              </TableCell>
                              <TableCell>
                                <Box
                                  component="input"
                                  disabled={!checked}
                                  value={seleccion[key]?.id_ref || ""}
                                  onChange={(e) => setCampo(key, "id_ref", e.target.value)}
                                  sx={{ ...inputCompactoSx, width: 60 }}
                                />
                              </TableCell>
                              <TableCell>
                                <Box
                                  component="input"
                                  disabled={!checked}
                                  value={seleccion[key]?.placa || ""}
                                  onChange={(e) => setCampo(key, "placa", e.target.value)}
                                  sx={{ ...inputCompactoSx, width: 60 }}
                                />
                              </TableCell>
                            </>
                          ) : (
                            <TableCell>
                              <Box
                                component="input"
                                disabled={!checked}
                                value={seleccion[key]?.observacion || ""}
                                onChange={(e) => setCampo(key, "observacion", e.target.value)}
                                sx={{ ...inputCompactoSx, width: "100%" }}
                              />
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center" sx={{ mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                Página {total ? page : 0} / {total ? totalPages : 0}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<NavigateBeforeIcon />}
                disabled={loading || page <= 1}
                onClick={() => cargar(page - 1)}
                sx={{ minHeight: 26, height: 26, fontSize: 11 }}
              >
                Anterior
              </Button>
              <Button
                size="small"
                variant="outlined"
                endIcon={<NavigateNextIcon />}
                disabled={loading || page >= totalPages}
                onClick={() => cargar(page + 1)}
                sx={{ minHeight: 26, height: 26, fontSize: 11 }}
              >
                Siguiente
              </Button>
            </Stack>
          </SectionCard>
        </Grid>

        {/* ── Columna derecha: equipos ya asignados a la OT + resumen ── */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={1.5}>
            <SectionCard
              title="Equipos en la Orden"
              icon={<AssignmentTurnedInIcon fontSize="small" />}
              color="success"
              extra={
                <Chip
                  size="small"
                  color="success"
                  label={asignados.radios.length + asignados.baterias.length}
                  sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
                />
              }
            >
              <EquiposAsignadosOT ref={equiposAsignadosRef} cod_ot={cod_ot} onData={setAsignados} />
            </SectionCard>

            <SectionCard title="Resumen de Orden" icon={<SummarizeIcon fontSize="small" />} color="secondary">
              <Stack spacing={0.5}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary">Total Radios</Typography>
                  <Typography variant="body2" fontWeight={700}>{asignados.radios.length}</Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary">Total Baterías</Typography>
                  <Typography variant="body2" fontWeight={700}>{asignados.baterias.length}</Typography>
                </Box>
              </Stack>
            </SectionCard>
          </Stack>
        </Grid>
      </Grid>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert onClose={() => setOpenSnackbar(false)} severity="success" variant="filled" sx={{ width: "100%" }}>
          Equipos asignados correctamente.
        </MuiAlert>
      </Snackbar>

      <CentroEquipoDetalleDialog
        open={!!equipoDialog}
        tipo={equipoDialog?.tipo}
        identificador={equipoDialog?.identificador}
        onClose={() => setEquipoDialog(null)}
      />
    </Box>
  );
};

export default AsignarEquiposParOT;
